import {
  type HubCreateCameraExampleStreamConfig,
  type HubCreateExampleStream,
  HubExampleStreamDefinitionType,
  type HubSampleFiltersUnionPart,
  type HubUpdateExampleStream,
} from '@robovision/hub'
import { CameraConfigurationKindIds } from './CameraConfigurationKind'
import type { ConfiguredCameraData } from './ConfiguredCameraData'
import {
  TriggerMode,
  triggerModeToHubTriggerMode,
} from './TriggerMode'


async function fileToString (file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject

    reader.readAsText(file)
  })
}

export interface CameraConfigurationDataParams {
  definitionId?: string
  definitionType?: HubExampleStreamDefinitionType
  name?: string
}

export interface CameraConfigurationCameraDataParams extends CameraConfigurationDataParams {
  configuredCameras?: ConfiguredCameraData[]
  triggerMode?: TriggerMode
}

export interface CameraConfigurationDatapoolDataParams extends CameraConfigurationDataParams {
  datapoolId?: string
  sampleFilters?: HubSampleFiltersUnionPart[]
  simulatedFps: number
}

export abstract class CameraConfigurationData {
  public definitionType: HubExampleStreamDefinitionType
  public definitionId: string
  public name: string

  constructor (params: CameraConfigurationDataParams) {
    this.definitionType = params.definitionType ?? HubExampleStreamDefinitionType.EXAMPLE_DEFINITION
    this.definitionId = params.definitionId ?? ''
    this.name = params.name ?? ''
  }

  public abstract toCreateBody (): Promise<HubCreateExampleStream>
  public abstract toUpdateBody (original: CameraConfigurationData): Promise<HubUpdateExampleStream>
}

export class CameraConfigurationCameraData extends CameraConfigurationData {
  public configuredCameras: ConfiguredCameraData[]
  public triggerMode: TriggerMode

  constructor (params: CameraConfigurationCameraDataParams) {
    super(params)
    this.configuredCameras = params.configuredCameras ?? []
    this.triggerMode = params.triggerMode ?? TriggerMode.CameraConfig
  }

  public async toCameras (
    configuredCameras: ConfiguredCameraData[] = this.configuredCameras,
  ): Promise<HubCreateCameraExampleStreamConfig[]> {
    return await Promise.all(configuredCameras.map(async (camera) => ({
      cameraId: camera.camera.id,
      configData: camera.configFile ? await fileToString(camera.configFile) : '',
      configName: camera.configName ?? '',
      viewName: camera.viewName,
      viewPath: camera.viewPath,
    })))
  }

  public async toCreateBody (): Promise<HubCreateExampleStream> {
    const cameras = await this.toCameras()

    return {
      config: {
        cameras,
        kind: CameraConfigurationKindIds.Camera,
        triggerMode: triggerModeToHubTriggerMode(this.triggerMode),
      },
      definitionId: this.definitionId ?? '',
      definitionType: this.definitionType,
      name: this.name ?? '',
    }
  }

  public async toUpdateBody (original: CameraConfigurationData): Promise<HubUpdateExampleStream> {
    if (!(original instanceof CameraConfigurationCameraData)) {
      throw new Error('Original configuration must be of the same type')
    }

    const cameras = await this.toCameras()
    const originalCameras = await this.toCameras(original.configuredCameras)

    return {
      config: JSON.stringify(cameras) !== JSON.stringify(originalCameras) || this.triggerMode !== original.triggerMode
        ? {
          cameras,
          kind: CameraConfigurationKindIds.Camera,
          triggerMode: triggerModeToHubTriggerMode(this.triggerMode),
        } : undefined,
      definitionId: this.definitionId !== original.definitionId
        ? this.definitionId
        : undefined,
      definitionType: this.definitionType !== original.definitionType
        ? this.definitionType
        : undefined,
      name: this.name !== original.name
        ? this.name
        : undefined,
    }
  }
}

export class CameraConfigurationDatapoolData extends CameraConfigurationData {
  public datapoolId?: string
  public sampleFilters?: HubSampleFiltersUnionPart[]
  public simulatedFps: number

  constructor (params: CameraConfigurationDatapoolDataParams) {
    super(params)
    this.datapoolId = params.datapoolId
    this.sampleFilters = params.sampleFilters
    this.simulatedFps = params.simulatedFps
  }

  public async toCreateBody (): Promise<HubCreateExampleStream> {
    if (!this.datapoolId) {
      throw new Error('Datapool ID is required for creating datapool example stream')
    }

    return {
      config: {
        datapoolId: this.datapoolId,
        kind: CameraConfigurationKindIds.Datapool,
        sampleFilters: this.sampleFilters,
        simulatedFps: this.simulatedFps,
      },
      definitionId: this.definitionId ?? '',
      definitionType: this.definitionType,
      name: this.name ?? '',
    }
  }

  public async toUpdateBody (original: CameraConfigurationData): Promise<HubUpdateExampleStream> {
    if (!(original instanceof CameraConfigurationDatapoolData)) {
      throw new Error('Original configuration must be of the same type')
    }

    const configChanged =
      this.datapoolId !== original.datapoolId ||
      JSON.stringify(this.sampleFilters) !== JSON.stringify(original.sampleFilters) ||
      this.simulatedFps !== original.simulatedFps

    return {
      config: configChanged && this.datapoolId
        ? {
          datapoolId: this.datapoolId,
          kind: CameraConfigurationKindIds.Datapool,
          sampleFilters: this.sampleFilters,
          simulatedFps: this.simulatedFps,
        } : undefined,
      definitionId: this.definitionId !== original.definitionId
        ? this.definitionId
        : undefined,
      definitionType: this.definitionType !== original.definitionType
        ? this.definitionType
        : undefined,
      name: this.name !== original.name
        ? this.name
        : undefined,
    }
  }
}
